import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, FileText } from 'lucide-react';

const AdminSectionsPage = () => {
  const { getPageSections, createPageSection, updatePageSection, deletePageSection } = useCMS();
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedPage, setSelectedPage] = useState('home');
  const [formData, setFormData] = useState({
    page: 'home', section_key: '', title: '', subtitle: '', content: '', image: '', order: 0
  });

  const pages = ['home', 'about', 'solutions', 'contact'];

  useEffect(() => { loadAllSections(); }, []);

  const loadAllSections = async () => {
    try {
      const allSections = {};
      for (const page of pages) {
        const data = await getPageSections(page, false);
        allSections[page] = data;
      }
      setSections(allSections);
    } catch (e) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updatePageSection(editingId, formData);
        toast.success('Section updated');
      } else {
        await createPageSection(formData);
        toast.success('Section created');
      }
      resetForm();
      loadAllSections();
    } catch (e) {
      toast.error(e.message || 'Failed to save section');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await deletePageSection(id);
      toast.success('Section deleted');
      loadAllSections();
    } catch (e) {
      toast.error('Failed to delete section');
    }
  };

  const handleEdit = (section) => {
    setFormData({
      page: section.page,
      section_key: section.section_key,
      title: section.title || '',
      subtitle: section.subtitle || '',
      content: section.content || '',
      image: section.image || '',
      order: section.order
    });
    setEditingId(section.id);
    setShowForm(true);
  };

  const handleToggleActive = async (section) => {
    try {
      await updatePageSection(section.id, { is_active: !section.is_active });
      toast.success(`Section ${section.is_active ? 'hidden' : 'shown'}`);
      loadAllSections();
    } catch (e) {
      toast.error('Failed to update section');
    }
  };

  const resetForm = () => {
    setFormData({ page: selectedPage, section_key: '', title: '', subtitle: '', content: '', image: '', order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Page Sections</h1>
          <p className="text-text-secondary mt-1">Manage content sections across all pages</p>
        </div>
        <Button onClick={() => { setFormData({ ...formData, page: selectedPage }); setShowForm(true); }} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Section
        </Button>
      </div>

      {/* Page Tabs */}
      <div className="flex space-x-2 border-b border-border-line">
        {pages.map((page) => (
          <button
            key={page}
            onClick={() => setSelectedPage(page)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              selectedPage === page 
                ? 'text-tech-blue border-b-2 border-tech-blue' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {page}
            <span className="ml-2 text-xs bg-page-bg px-2 py-0.5 rounded-full">
              {sections[page]?.length || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg border-border-line my-8">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Section</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Page *</label>
                    <select
                      value={formData.page}
                      onChange={(e) => setFormData({ ...formData, page: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      disabled={editingId}
                    >
                      {pages.map(p => (
                        <option key={p} value={p} className="capitalize">{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Section Key *</label>
                    <input
                      type="text"
                      required
                      value={formData.section_key}
                      onChange={(e) => setFormData({ ...formData, section_key: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      placeholder="e.g., hero, features, cta"
                      disabled={editingId}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Content</label>
                  <textarea
                    rows={4}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Image URL</label>
                  <input
                    type="text"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-4">
        {(sections[selectedPage] || []).sort((a, b) => a.order - b.order).map((section) => (
          <Card 
            key={section.id} 
            className={`border-border-line ${!section.is_active ? 'opacity-50 border-l-4 border-l-yellow-400' : ''}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText size={16} className="text-tech-blue" />
                    <span className="font-mono text-sm bg-tech-blue/10 text-tech-blue px-2 py-1 rounded">
                      {section.section_key}
                    </span>
                    <span className="text-xs text-text-secondary">Order: {section.order}</span>
                    <span className={`text-xs px-2 py-1 rounded ${section.is_active ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                      {section.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </div>
                  {section.title && (
                    <h3 className="font-semibold text-text-primary mb-1">{section.title}</h3>
                  )}
                  {section.subtitle && (
                    <p className="text-sm text-text-secondary mb-2">{section.subtitle}</p>
                  )}
                  {section.content && (
                    <p className="text-sm text-text-secondary line-clamp-2">{section.content}</p>
                  )}
                </div>
                {section.image && (
                  <img src={section.image} alt="" className="w-20 h-20 object-cover rounded-lg ml-4" />
                )}
              </div>
              <div className="flex items-center justify-end mt-4 pt-3 border-t border-border-line space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleToggleActive(section)}
                  className="text-xs"
                >
                  {section.is_active ? 'Hide' : 'Show'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleEdit(section)} className="text-xs">
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(section.id)} className="text-xs text-red-500 hover:text-red-600">
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(!sections[selectedPage] || sections[selectedPage].length === 0) && (
        <div className="text-center py-12 text-text-secondary">
          No sections for {selectedPage} page yet. Click "Add Section" to create one.
        </div>
      )}
    </div>
  );
};

export default AdminSectionsPage;
